import axios from 'axios';
import { BASE_PATH_SERVER } from './config';
import { API_PATH } from 'config';

// Todo el tráfico pasa por axios para heredar el interceptor global
// (`axiosInterceptors.js`): inyecta Authorization + X-Tenant-Subdomain y
// reintenta una vez ante 401 refrescando el token (arregla la race de la
// primera carga). No se setea el header de auth a mano: lo pone el interceptor.

// Objeto de error de red (sin respuesta del backend). Mantiene el contrato de
// la versión fetch previa, que ante fallo de red devolvía esta forma.
const clientError = (err) => ({
  status: 'client',
  codeMessage: 'ERROR_EN_LA_PETICION',
  message: err?.message,
});

/**
 * Traduce la respuesta de error del backend a lo que consume el front.
 *
 * Antes leía únicamente `data.error`, un campo que la API **no devuelve nunca**: el
 * manejador de errores del backend responde `{message}` y los endpoints con código de
 * negocio responden `{code, mensaje}`. El resultado era que toda falla se le mostraba a la
 * persona como "Error desconocido" — incluida la más común del cambio de contraseña
 * ("Tu contraseña no es correcta"), que quedaba como "Error 403: Prohibido - Error
 * desconocido".
 *
 * Se conserva `error` al final por si algún endpoint lo usara; y se propaga `code`, que es
 * lo que permite al caller distinguir un caso de otro sin comparar textos.
 */
const errorDeRespuesta = (response) => {
  const data = response.data || {};
  return {
    status: response.status,
    code: data.code,
    message: data.mensaje || data.message || data.error || 'Error desconocido',
  };
};

// get/post/put/delete: devuelven el body parseado y NUNCA rechazan. Igual que
// la versión con fetch (que no lanzaba en 4xx), ante una respuesta de error del
// backend se devuelve su body (los callers inspeccionan result.state / status /
// message); solo un error de red devuelve el objeto { status: 'client' }.
export const getApi = async (dst, params) => {
  let url = `${BASE_PATH_SERVER}${dst}`;
  if (params) {
    url = url + '/' + params.join('/');
  }
  try {
    const { data } = await axios.get(url);
    return data;
  } catch (err) {
    return err.response ? err.response.data : clientError(err);
  }
};

export const postApi = async (dst, body) => {
  const url = `${BASE_PATH_SERVER}${dst}`;
  try {
    const { data } = await axios.post(url, body);
    return data;
  } catch (err) {
    return err.response ? err.response.data : clientError(err);
  }
};

export const putApi = async (dst, body) => {
  const url = `${BASE_PATH_SERVER}${dst}`;
  try {
    const { data } = await axios.put(url, body);
    return data;
  } catch (err) {
    return err.response ? err.response.data : clientError(err);
  }
};

export const deleteApi = async (dst) => {
  const url = `${BASE_PATH_SERVER}${dst}`;
  try {
    const { data } = await axios.delete(url);
    return data;
  } catch (err) {
    return err.response ? err.response.data : clientError(err);
  }
};

// postToApi/putToAPi: RECHAZAN ante non-2xx (contrato previo). El caller maneja
// el throw. Se preserva la forma { status, message } del error.
export const postToApi = async (_url, body) => {
  const url = `${API_PATH}${_url}`;
  try {
    const { data } = await axios.post(url, body);
    return data;
  } catch (err) {
    if (err.response) {
      throw errorDeRespuesta(err.response);
    }
    throw err;
  }
};

export const putToAPi = async (_url, body) => {
  const url = `${API_PATH}${_url}`;
  try {
    const { data } = await axios.put(url, body);
    return data;
  } catch (err) {
    if (err.response) {
      throw errorDeRespuesta(err.response);
    }
    throw err;
  }
};
